/**
 * Bet Ticker — `POST /v1/book/ticker`.
 *
 * Server-side filtering and paging: the table shows what the query returned,
 * so the row count in the header is the real total rather than the size of
 * whatever happened to be cached on the client.
 *
 * Demo sessions read the fixture repository, which applies the same rules.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { betFetch, getCallerAgentId, hasLiveApi, hasLiveBet } from '@/api/client'
import { useSession } from '@/api/hooks/shared/useSession'
import { useBookmakerSessionFlag } from '@/api/hooks/shared/useStaffMe'
import { keys } from '@/api/hooks/shared/keys'
import { ROLE_LABEL, type Role } from '@/data/types'
import {
  tickerMarketTypeForFlag,
  type BookmakerSessionFlag,
} from '@/lib/bookmakerSessionFlag'
import {
  demoFilterOptions,
  queryDemoTicker,
} from '@/features/book-management/adapters/mockRepository'
import type {
  BetSource,
  TickerBet,
  TickerFilterOptions,
  TickerFilters,
  TickerPage,
  TickerStatusScope,
} from '@/features/book-management/domain'
import {
  tickerFilterOptionsPath,
  tickerListPath,
  type TickerBackend,
} from '@/features/book-management/ticker/tickerBackend'

/** Open bets move; a few seconds of lag is the point of a ticker. */
export const TICKER_POLL_MS = 5_000

interface ApiTickerRow {
  betId: string
  placedAt: string | null
  matchedAt: string | null
  memberId: string
  userName: string
  source: BetSource
  eventType: string
  eventName: string
  marketId: string
  marketName: string
  selectionName: string
  side: string
  currencyType: string
  odds: number
  oddsPlaced?: number
  oddsMatched?: number
  matchedAmount: number
  unmatchedAmount: number
  /** Lab getAllBets `multiplier` (default 1). */
  multiplier?: number
  clientIp: string
  /** Lab getAllBets `doubtfull` — optional server overlay. */
  doubtfull?: boolean
  /** Lab getAllBets `watchlist` — optional server overlay. */
  watchlist?: boolean
  isFlagged?: boolean
  /** Lab `reason` — IncorrectPrice / Sniping / LowLiquidity. */
  reason?: string
  isBetfairFlagged?: boolean
}

interface ApiTickerResponse {
  rows: ApiTickerRow[]
  page: number
  pageSize: number
  total: number
  asOf: string
}

export interface TickerQuery {
  source: BetSource
  statusScope: TickerStatusScope
  filters: TickerFilters
  /**
   * `bq` → Lab Betfair 7-day switch / Previous archive (`POST /v1/book/tickerBQ`).
   * Default `pg` → live Postgres ticker.
   */
  dataSource?: 'pg' | 'bq'
  /** Previous needs a range; the query stays idle until one is chosen. */
  enabled?: boolean
}

function levelLabel(role: Role | undefined): string {
  return role ? ROLE_LABEL[role] : 'Master'
}

/** ISO day → instant. `to` covers the whole day the operator picked. */
function dayStart(day: string): string {
  return `${day}T00:00:00.000Z`
}

function dayEnd(day: string): string {
  return `${day}T23:59:59.999Z`
}

/** Effective Lab marketType after staff Market access (when chip is All). */
export function effectiveTickerMarketType(
  filters: TickerFilters,
  bookmakerSessionFlag: BookmakerSessionFlag = 'all',
): string | undefined {
  if (filters.marketType !== 'all') return filters.marketType
  return tickerMarketTypeForFlag(bookmakerSessionFlag)
}

/** Build Agent-API ticker POST body (whitebox-tested). */
export function buildTickerRequestBody(
  query: TickerQuery,
  user: string,
  role: Role | undefined,
  bookmakerSessionFlag: BookmakerSessionFlag = 'all',
) {
  const { filters } = query
  const marketType = effectiveTickerMarketType(filters, bookmakerSessionFlag)
  return {
    user,
    accessLevel: levelLabel(role),
    source: query.source,
    statusScope: query.statusScope,
    page: filters.page,
    pageSize: filters.pageSize,
    ...(filters.from ? { fromDate: dayStart(filters.from) } : {}),
    ...(filters.to ? { toDate: dayEnd(filters.to) } : {}),
    ...(filters.members.length
      ? { userName: filters.members }
      : filters.search
        ? { userName: filters.search }
        : {}),
    ...(filters.sport !== 'all' ? { eventType: filters.sport } : {}),
    ...(filters.event ? { eventName: filters.event } : {}),
    ...(filters.market ? { marketName: filters.market } : {}),
    ...(marketType ? { marketType } : {}),
    ...(bookmakerSessionFlag !== 'all' ? { bookmakerSessionFlag } : {}),
    ...(filters.currency !== 'all' ? { currencyType: filters.currency } : {}),
    ...(filters.side !== 'all' ? { side: filters.side } : {}),
    ...(filters.status !== 'all' ? { status: filters.status } : {}),
    ...(filters.stakeMin != null ? { stakeFrom: filters.stakeMin } : {}),
    ...(filters.stakeMax != null ? { stakeTo: filters.stakeMax } : {}),
  }
}

/** Map Agent-API ticker row → UI bet (side coerce + numeric defaults). */
export function toTickerBet(row: ApiTickerRow): TickerBet {
  return {
    id: row.betId,
    placedAt: row.placedAt,
    matchedAt: row.matchedAt,
    memberId: row.memberId ?? '',
    memberUsername: row.userName ?? '',
    source: row.source,
    sport: row.eventType ?? '',
    eventName: row.eventName ?? '',
    marketId: row.marketId ?? '',
    marketName: row.marketName ?? '',
    selection: row.selectionName ?? '',
    side: String(row.side).toUpperCase() === 'LAY' ? 'LAY' : 'BACK',
    currency: row.currencyType ?? '',
    odds: Number(row.odds) || 0,
    oddsPlaced: Number(row.oddsPlaced ?? row.odds) || 0,
    oddsMatched: Number(row.oddsMatched ?? row.odds) || 0,
    matchedAmount: Number(row.matchedAmount) || 0,
    unmatchedAmount: Number(row.unmatchedAmount) || 0,
    multiplier: (() => {
      const n = Number(row.multiplier)
      return Number.isFinite(n) && n > 0 ? n : 1
    })(),
    clientIp: row.clientIp || null,
    doubtfull: Boolean(row.doubtfull),
    watchlist: Boolean(row.watchlist),
    isFlagged: Boolean(row.isFlagged),
    reason: row.reason ?? '',
    isBetfairFlagged: Boolean(row.isBetfairFlagged),
  }
}

/** Agent tickerBQ / filter-options Standard `{ status, message, data }` (PG ticker stays flat). */
export type StandardApiOk<T> = { status?: number; message?: string; data?: T }

/** Peel Standard envelope once; flat PG responses pass through. */
export function unwrapStandardData<T extends object>(
  raw: T | StandardApiOk<T> | null | undefined,
): T {
  if (raw && typeof raw === 'object' && 'data' in raw && (raw as StandardApiOk<T>).data != null) {
    return (raw as StandardApiOk<T>).data as T
  }
  return raw as T
}

/** Live Agent ticker fetch (blackbox-tested for path + Standard unwrap). */
export async function fetchBookTicker(
  query: TickerQuery,
  accountId: string | undefined,
  role: Role | undefined,
  bookmakerSessionFlag: BookmakerSessionFlag = 'all',
): Promise<TickerPage> {
  if (!hasLiveApi()) {
    return queryDemoTicker(query.source, query.statusScope, query.filters)
  }

  const user = getCallerAgentId() ?? accountId
  if (!hasLiveBet() || !user) {
    return {
      rows: [],
      total: 0,
      page: query.filters.page,
      pageSize: query.filters.pageSize,
      asOf: new Date().toISOString(),
    }
  }

  const dataSource = (query.dataSource ?? 'pg') as TickerBackend
  const path = tickerListPath(dataSource)
  const raw = await betFetch<ApiTickerResponse | StandardApiOk<ApiTickerResponse>>(
    path,
    {
      method: 'POST',
      body: JSON.stringify(
        buildTickerRequestBody(query, user, role, bookmakerSessionFlag),
      ),
    },
  )
  const res = unwrapStandardData(raw)

  return {
    rows: (res?.rows ?? []).map(toTickerBet),
    total: res?.total ?? 0,
    page: res?.page ?? query.filters.page,
    pageSize: res?.pageSize ?? query.filters.pageSize,
    asOf: res?.asOf ?? new Date().toISOString(),
  }
}

/** Live filter-options fetch (blackbox-tested for tickerBQ path + cascade body). */
export async function fetchBookTickerFilterOptions(opts: {
  source: BetSource
  statusScope: TickerStatusScope
  dataSource?: TickerBackend
  from?: string
  to?: string
  eventType?: string
  eventName?: string
  user: string
  role: Role | undefined
}): Promise<TickerFilterOptions> {
  const dataSource = opts.dataSource ?? 'pg'
  const path = tickerFilterOptionsPath(dataSource)
  const raw = await betFetch<TickerFilterOptions | StandardApiOk<TickerFilterOptions>>(
    path,
    {
      method: 'POST',
      body: JSON.stringify({
        user: opts.user,
        accessLevel: levelLabel(opts.role),
        source: opts.source,
        statusScope: opts.statusScope,
        ...(opts.from ? { fromDate: dayStart(opts.from) } : {}),
        ...(opts.to ? { toDate: dayEnd(opts.to) } : {}),
        ...(opts.eventType ? { eventType: opts.eventType } : {}),
        ...(opts.eventName ? { eventName: opts.eventName } : {}),
      }),
    },
  )
  const res = unwrapStandardData(raw)
  return {
    sports: res?.sports ?? [],
    events: res?.events ?? [],
    markets: res?.markets ?? [],
  }
}

/**
 * Filters that belong in the cache key.
 *
 * Page and size are separate so a page turn is a distinct entry, and
 * `keepPreviousData` can hold the old rows while the next page lands.
 */
function filterKey(filters: TickerFilters): string {
  return JSON.stringify({
    members: filters.members,
    search: filters.search,
    sport: filters.sport,
    event: filters.event,
    market: filters.market,
    marketType: filters.marketType,
    side: filters.side,
    status: filters.status,
    currency: filters.currency,
    from: filters.from,
    to: filters.to,
    stakeMin: filters.stakeMin,
    stakeMax: filters.stakeMax,
  })
}

export function useBookTicker(query: TickerQuery) {
  const { data: session } = useSession()
  const accountId = session?.account.id
  const role = session?.account.role
  const bookmakerSessionFlag = useBookmakerSessionFlag()
  // Settled history does not change while you look at it; only the live ticker
  // polls, and React Query will not start a request while one is in flight.
  const live = hasLiveApi() && hasLiveBet()
  const poll = live && query.statusScope === 'current'
  const enabled = Boolean(session) && query.enabled !== false

  const dataSource = query.dataSource ?? 'pg'

  return useQuery({
    queryKey: keys.bookTicker({
      user: getCallerAgentId() ?? accountId,
      level: levelLabel(role),
      source: query.source,
      statusScope: query.statusScope,
      dataSource,
      page: query.filters.page,
      pageSize: query.filters.pageSize,
      filters: filterKey(query.filters),
      bookmakerSessionFlag,
    }),
    queryFn: () => fetchBookTicker(query, accountId, role, bookmakerSessionFlag),
    enabled,
    refetchInterval: poll ? TICKER_POLL_MS : false,
    // Polling a tab nobody is looking at spends the operator's rate limit.
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
  })
}

export function useBookTickerFilterOptions(query: {
  source: BetSource
  statusScope: TickerStatusScope
  from?: string
  to?: string
  /** `bq` → Lab getEventsForBetTickerNew / tickerBQ filter-options. */
  dataSource?: 'pg' | 'bq'
  eventType?: string
  eventName?: string
  enabled?: boolean
}) {
  const { data: session } = useSession()
  const accountId = session?.account.id
  const role = session?.account.role
  const dataSource = query.dataSource ?? 'pg'

  return useQuery({
    queryKey: keys.bookTickerFilterOptions({
      user: getCallerAgentId() ?? accountId,
      level: levelLabel(role),
      source: query.source,
      statusScope: query.statusScope,
      dataSource,
      from: query.from,
      to: query.to,
      eventType: query.eventType,
      eventName: query.eventName,
    }),
    queryFn: async (): Promise<TickerFilterOptions> => {
      if (!hasLiveApi()) return demoFilterOptions(query.source, query.statusScope)

      const user = getCallerAgentId() ?? accountId
      if (!hasLiveBet() || !user) return { sports: [], events: [], markets: [] }

      return fetchBookTickerFilterOptions({
        source: query.source,
        statusScope: query.statusScope,
        dataSource,
        from: query.from,
        to: query.to,
        eventType: query.eventType,
        eventName: query.eventName,
        user,
        role,
      })
    },
    enabled: Boolean(session) && query.enabled !== false,
    // Pickers do not need to keep pace with the ticker.
    staleTime: 60_000,
  })
}
